import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
// Removed unused Card imports
import { useToast } from '../components/ui/use-toast';
import { Search, ShoppingCart, Check, X, Clock, CheckCircle, AlertCircle, MapPin, Users, UtensilsCrossed, Ban, ArrowLeft, AlertTriangle, Info, ChevronDown, History, Home, CalendarDays, ClipboardList, ZoomIn, ZoomOut, Maximize2, RotateCcw, ArrowRight, Armchair } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { confirm } from '../components/ui/confirm-dialog';
import orderService from '../services/orderService';
import { getAllMenus, isMenuAvailableNow, getMenuById } from '../services/menuService';
import { withGuestPrices } from '../lib/taxMath';
import { floorPlanService } from '../services/floorPlanService';
import SeatingFloorView from '../components/SeatingFloorView';
import { useAppSettings } from '../context/AppSettingsContext';
import { useRestaurant } from '../context/RestaurantContext';
import { useT } from '../context/LanguageContext';
import { dietMeta } from '../lib/diet';
import { sanitizeMuLocal, toMuPhone } from '../lib/muPhone';

// A serving-time window to a short label, returning '' for an all-day window so callers can skip the chip.
// `t` is threaded in because this lives outside the component, where hooks can't run.
function fmtWindow(t, from, until, follow) {
  if (follow) return t('orders.window.branchHours');
  if (from && until) return `${from}–${until}`;
  if (from) return t('orders.window.from', { time: from });
  if (until) return t('orders.window.until', { time: until });
  return '';
}

/* WHERE the order came from. A table-less order sits on the branch's ONLINE
   counter, which prints as "Table ONLINE" if the table is rendered straight —
   and a kiosk order and one placed on a guest's own phone are the same shape
   of order on that same counter. `channel` is the door the server recorded at
   creation; only the LABEL is translated, never the value. `t` is threaded in
   because this lives outside the component. */
const CHANNEL_KEYS = {
  KIOSK: 'orders.channel.kiosk',
  APP: 'orders.channel.app',
  COUNTER: 'orders.channel.counter',
  DELIVERY: 'orders.channel.delivery',
};
function orderOrigin(t, order) {
  /* The floor-plan NAME (table_code): the only identity a table has. */
  const num = String(order?.table_code || '').trim();
  // Orders placed before the channel was recorded fall back to "Online".
  if (!num || num.toUpperCase() === 'ONLINE') {
    /* A named kiosk says WHICH machine. "Kiosk" alone is no use to a branch
       running three of them: naming it is the difference between knowing an
       order is waiting and knowing where to walk. */
    const kiosk = (order?.kiosk_name || '').trim();
    if (kiosk) return t('orders.channel.kioskNamed', { name: kiosk });
    return t(CHANNEL_KEYS[order?.channel] || 'orders.channel.online');
  }
  return t('orders.channel.table', { number: num });
}

// Left-rail navigation of the POS shell, mirroring a till's fixed nav with Home first.
// Labels are translation KEYS — the rail resolves them at render, inside the component.
const POS_NAV = [
  { key: 'create', labelKey: 'orders.nav.home',          icon: Home,          to: '/order' },
  { key: 'resv',   labelKey: 'orders.nav.reservations',  icon: CalendarDays,  to: '/reservations' },
  { key: 'active', labelKey: 'orders.nav.activeOrders',  icon: ClipboardList, to: '/orders/active' },
  { key: 'orders', labelKey: 'orders.nav.allOrders',     icon: History,       to: '/orders/all' },
];

// Steps the waiter walks through once a table is picked.
const POS_STEPS = [
  { key: 'table',  labelKey: 'orders.step.table' },
  { key: 'guests', labelKey: 'orders.step.guests' },
  { key: 'menu',   labelKey: 'orders.step.menu' },
  { key: 'pay',    labelKey: 'orders.step.pay' },
];

// Statuses that have a translated badge label; anything else falls back to the raw server value.
const LABELLED_STATUSES = [
  'pending', 'ordered', 'confirmed', 'preparing', 'ready',
  'served', 'delivered', 'completed', 'cancelled', 'held',
];

const ZOOM_MIN = 0.4;
const ZOOM_MAX = 1.6;

function OrderManagement({ restaurantId, activeTab: parentActiveTab }) {
  const navigate = useNavigate();
  const { t } = useT();
  const { formatDateTime, formatCurrency, settings } = useAppSettings();
  // Which dish fields the order-taking menu shows (Settings → Menu View).
  const menuFields = settings?.order_menu_fields || { image: true, description: true, category: true, price: true };
  const { restaurantId: ctxRestaurantId, locationId, location } = useRestaurant();
  const effectiveRestaurantId = restaurantId || ctxRestaurantId || 1;
  const [activeTab, setActiveTab] = useState(parentActiveTab || 'create');
  const [orders, setOrders] = useState([]);
  // table id -> {isShared, sessions} from the with-orders feed, so the list can group and flag shared tables.
  const [tableInfo, setTableInfo] = useState({});
  // The seating tree for the active location, plus the drilled-into floor/section and a table-busy map.
  const [floors, setFloors] = useState([]);
  const [selectedFloorId, setSelectedFloorId] = useState(null);
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [tableBusy, setTableBusy] = useState({});
  const [selectedTable, setSelectedTable] = useState(null);
  // A tapped table waits in the action bar until "Next", so a mis-tap never drops the waiter into the builder.
  const [pendingTable, setPendingTable] = useState(null);
  // Floor-map zoom; "Fit" measures the canvas viewport against the plan size.
  const [zoom, setZoom] = useState(1);
  const canvasWrapRef = useRef(null);
  const [cart, setCart] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // Order flow: pick a table, confirm party size, then load the location's real menu for ordering.
  const [partySize, setPartySize] = useState('');
  // Optional guest identity; phone is the customer's unique key, and the backend links or creates their profile.
  const [guestName, setGuestName] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [partyConfirmed, setPartyConfirmed] = useState(false);
  // Menu list (metadata only) for this location, from getAllMenus.
  const [orderMenu, setOrderMenu] = useState([]);
  const [orderMenuLoading, setOrderMenuLoading] = useState(false);
  // Show menus that aren't orderable right now (inactive / outside their window).
  const [showInactiveMenus, setShowInactiveMenus] = useState(false);
  // Which menu the user has drilled into; null shows the menu list first.
  const [selectedMenuId, setSelectedMenuId] = useState(null);
  // The picked menu's full content (sections → items → variants), loaded on select.
  const [selectedMenuContent, setSelectedMenuContent] = useState(null);
  const [menuContentLoading, setMenuContentLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  // The dish row expanded inline to pick a variant and quantity; null means all rows are collapsed.
  const [expandedId, setExpandedId] = useState(null);
  const [expVariantId, setExpVariantId] = useState(null);
  const [expQty, setExpQty] = useState(1);
  // Selected complimentary items for the expanded dish, one pick per kind, all of which are mandatory.
  const [expComp, setExpComp] = useState({});
  const [expCompOptions, setExpCompOptions] = useState([]);
  const [expCompLoading, setExpCompLoading] = useState(false);
  // Selected paid add-ons for the expanded dish, priced per the chosen variant and cleared when it changes.
  const [expAddons, setExpAddons] = useState({});
  const [placedOrderNumber, setPlacedOrderNumber] = useState('');
  const { toast } = useToast();


  // Keep the view in sync with the route prop, including the undefined case, since React reuses the instance.
  useEffect(() => {
    const tabMapping = { create: 'create', list: 'orders', active: 'active' };
    setActiveTab(tabMapping[parentActiveTab] || 'create');
  }, [parentActiveTab]);

  const loadTables = useCallback(async () => {
    if (!effectiveRestaurantId || !locationId) {
      setFloors([]);
      return;
    }
    setIsLoading(true);
    try {
      // The floor plan gives the table tree and the with-orders list marks busy tables; a 404 means an empty plan.
      const [plan, withOrders] = await Promise.all([
        floorPlanService.get(effectiveRestaurantId, locationId).catch(() => ({ floors: [] })),
        orderService.getTables(effectiveRestaurantId).catch(() => []),
      ]);
      const fl = plan?.floors || [];
      setFloors(fl);
      const busy = {};
      (withOrders || []).forEach((t) => {
        busy[t.id] = !!(t.has_active_order || t.has_open_session || t.occupancy_status === 'occupied');
      });
      setTableBusy(busy);
      // Default the drill-down to the first floor / its first section.
      setSelectedFloorId((prev) => prev ?? fl[0]?.id ?? null);
    } catch (error) {
      console.error('Error loading seating:', error);
      toast({
        title: 'Error',
        description: 'Failed to load tables. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast, effectiveRestaurantId, locationId]);

  const loadOrders = useCallback(async (mode = 'active') => {
    setIsLoading(true);
    try {
      // Orders plus the tables feed: Active shows unpaid orders only, All shows every order today.
      const [ordersData, tablesData] = await Promise.all([
        mode === 'active'
          ? orderService.getActiveOrders()
          : orderService.getOrdersForDay(),
        orderService.getTables(effectiveRestaurantId).catch(() => []),
      ]);
      const formattedOrders = ordersData.map(order => orderService.formatOrderForDisplay(order));
      setOrders(formattedOrders);
      const info = {};
      (tablesData || []).forEach((t) => {
        info[t.id] = {
          isShared: !!t.is_shared,
          sessions: t.open_sessions || [],
        };
      });
      setTableInfo(info);
    } catch (error) {
      console.error('Error loading orders:', error);
      toast({
        title: 'Error',
        description: 'Failed to load orders. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast, effectiveRestaurantId]);

  useEffect(() => {
    if (activeTab === 'create') {
      loadTables();
    } else if (activeTab === 'orders' || activeTab === 'active') {
      loadOrders(activeTab === 'active' ? 'active' : 'all');
    }
  }, [activeTab, loadTables, loadOrders]);


  // Commit the tapped table and reset the order flow back to the party-size step.
  const selectTable = (table) => {
    setSelectedTable(table);
    setPendingTable(null);
    setCart([]);
    // Default the guest count to the table's capacity so it's shown up front.
    setPartySize(String(table?.capacity || 2));
    setPartyConfirmed(false);
    setOrderMenu([]);
    setSelectedMenuId(null);
    setExpandedId(null);
    setSelectedCategory('all');
    setSearchQuery('');
    setPlacedOrderNumber('');
  };

  const resetOrderFlow = () => {
    setCart([]);
    setSelectedTable(null);
    setPendingTable(null);
    setPartySize('');
    setGuestName('');
    setGuestPhone('');
    setPartyConfirmed(false);
    setOrderMenu([]);
    setShowInactiveMenus(false);
    setSelectedMenuId(null);
    setSelectedMenuContent(null);
    setSelectedCategory('all');
    setSearchQuery('');
  };

  // Load this branch's menu list once a table and party size are set, letting the picker filter by time and state.
  useEffect(() => {
    if (!selectedTable || !partyConfirmed || !effectiveRestaurantId || !locationId) return;
    let cancelled = false;
    setOrderMenuLoading(true);
    getAllMenus(effectiveRestaurantId)
      .then((data) => {
        if (cancelled) return;
        const lid = Number(locationId);
        const list = (Array.isArray(data) ? data : []).filter(
          (m) => (m.location_ids || []).includes(lid),
        );
        setOrderMenu(list);
        // Jump into the menu that is live now, but only when there is exactly one, so an overlap still lets the waiter choose.
        const liveNow = list.filter((m) => isMenuAvailableNow(m));
        if (liveNow.length === 1) {
          setSelectedMenuId(liveNow[0].id);
          setSelectedCategory('all');
          setSearchQuery('');
        }
      })
      .finally(() => { if (!cancelled) setOrderMenuLoading(false); });
    return () => { cancelled = true; };
  }, [selectedTable, partyConfirmed, effectiveRestaurantId, locationId]);

  // Load the picked menu's content (sections → dishes) when one is selected.
  useEffect(() => {
    if (!selectedMenuId || !effectiveRestaurantId || !locationId) {
      setSelectedMenuContent(null);
      return undefined;
    }
    let cancelled = false;
    setMenuContentLoading(true);
    getMenuById(effectiveRestaurantId, locationId, selectedMenuId)
      // Prices as the guest pays them, with the net beside each (what the order line carries).
      .then((data) => { if (!cancelled) setSelectedMenuContent(withGuestPrices(data) || null); })
      .finally(() => { if (!cancelled) setMenuContentLoading(false); });
    return () => { cancelled = true; };
  }, [selectedMenuId, effectiveRestaurantId, locationId]);

  // A qty-aware cart keyed by menu item, variant and add-ons; paid add-ons bump the line's unit price.
  const addToCart = (item, variant, qty = 1, complimentary = [], addons = []) => {
    const addonTotal = addons.reduce((s, a) => s + (Number(a.price) || 0), 0);
    const unitPrice = (Number(variant.price) || 0) + addonTotal;
    // The same unit without its tax — the order carries this, and the bill
    // adds the tax back, so the guest pays exactly the price shown here.
    const unitNet = (Number(variant.price_net ?? variant.price) || 0)
      + addons.reduce((s, a) => s + (Number(a.price_net ?? a.price) || 0), 0);
    // Different add-on selections are distinct lines so prices don't collide.
    const addonKey = addons.map((a) => a.addon_id).sort((x, y) => x - y).join('.');
    const key = `${item.id}-${variant.id}${addonKey ? `-${addonKey}` : ''}`;
    setCart((prev) => {
      const existing = prev.find((c) => c.key === key);
      if (existing) {
        return prev.map((c) => (c.key === key ? { ...c, qty: c.qty + qty } : c));
      }
      return [...prev, {
        key,
        menu_section_item_id: item.id,
        dish_id: item.dish_id,
        variant_id: variant.id,
        dishName: item.name,
        variantName: variant.name,
        price: unitPrice,
        net: unitNet,
        qty,
        complimentary,
        addons,
      }];
    });
  };

  // Expand a dish row to reveal its inline variant chooser, defaulting to the first variant and qty 1.
  const toggleExpand = (item) => {
    setExpandedId((cur) => {
      if (cur === item.id) return null;
      setExpVariantId((item.variants || [])[0]?.id ?? null);
      setExpQty(1);
      setExpComp({});
      setExpAddons({});
      // Fetch this dish's complimentary options from the dish controller.
      setExpCompOptions([]);
      if (item.dish_id) {
        setExpCompLoading(true);
        orderService.getComplimentaryItems(effectiveRestaurantId, item.dish_id)
          .then((opts) => setExpCompOptions(Array.isArray(opts) ? opts : []))
          .finally(() => setExpCompLoading(false));
      }
      return item.id;
    });
  };

  // Distinct kinds among the complimentary options; one pick per kind is mandatory before the dish can be added.
  const expCompKinds = [...new Set(expCompOptions.map((c) => c.kind || 'other'))];
  const allComplimentaryChosen = expCompKinds.every((k) => expComp[k]);

  const confirmExpanded = (item) => {
    const variant = (item.variants || []).find((v) => v.id === expVariantId);
    if (!variant) return;
    if (!allComplimentaryChosen) {
      toast({
        title: 'Choose complimentary item',
        description: 'Please pick one complimentary item for each type.',
        variant: 'destructive',
      });
      return;
    }
    const complimentary = Object.values(expComp).filter(Boolean);
    const addons = Object.values(expAddons).filter(Boolean);
    addToCart(item, variant, expQty, complimentary, addons);
    setExpandedId(null);
  };

  const changeQty = (key, delta) => {
    setCart((prev) => prev
      .map((c) => (c.key === key ? { ...c, qty: c.qty + delta } : c))
      .filter((c) => c.qty > 0));
  };

  const removeFromCart = (key) => setCart((prev) => prev.filter((c) => c.key !== key));

  const getTotalAmount = () => cart.reduce((sum, c) => sum + c.price * c.qty, 0);

  const handleSaveOrder = async () => {
    if (cart.length === 0 || !selectedTable) return;
    try {
      // Link the order to the menu it was built from; an empty list lets the backend infer it, covering multi-menu carts.
      const menuIds = selectedMenuId ? [selectedMenuId] : [];
      const created = await orderService.createOrder({
        restaurant_id: effectiveRestaurantId,
        location_id: locationId,
        table_id: selectedTable.id,
        party_size: partySize ? Number(partySize) : null,
        customer_name: guestName.trim(),
        customer_phone: toMuPhone(guestPhone),
        menu_ids: menuIds,
        items: cart.map((c) => ({
          dish_id: c.dish_id,
          variant_id: c.variant_id,
          menu_section_item_id: c.menu_section_item_id,
          quantity: c.qty,
          unit_price: c.net ?? c.price,
          name: `${c.dishName} (${c.variantName})`,
          complimentary: (c.complimentary || []).map((x) => ({ dish_id: x.id, name: x.name, kind: x.kind })),
          addons: (c.addons || []).map((a) => ({ addon_id: a.addon_id, name: a.name, price: Number(a.price_net ?? a.price) || 0 })),
        })),
      });

      setPlacedOrderNumber(created?.order_number || '');
      // Close the order modal first so only the success dialog is shown.
      resetOrderFlow();
      setShowSuccess(true);
      // Refresh the busy map so the just-used table now shows as occupied.
      loadTables();
      setTimeout(() => setShowSuccess(false), 2500);
    } catch (error) {
      console.error('Error saving order:', error);
      toast({
        title: 'Error',
        description: error?.message || 'Failed to save order. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await orderService.updateOrderStatus(orderId, newStatus);
      
      // Update local state
      setOrders(prevOrders => 
        prevOrders.map(order => 
          order.id === orderId ? { ...order, status: newStatus } : order
        )
      );
      
      const order = orders.find(o => o.id === orderId);
      toast({
        title: 'Status Updated',
        description: `${order?.order_number} is now ${newStatus.toUpperCase()}`,
      });
      
      // If order is completed, refresh tables to update status
      if (newStatus === 'served') {
        setTimeout(() => loadTables(), 1000);
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      toast({
        title: 'Error',
        description: 'Failed to update order status',
        variant: 'destructive',
      });
    }
  };

  // Move one line item to the next kitchen stage: PENDING -> PREPARING -> READY -> SERVED.
  const ITEM_NEXT_STATUS = { PENDING: 'PREPARING', PREPARING: 'READY', READY: 'SERVED' };

  const updateItemStatus = async (order, item, newStatus) => {
    try {
      await orderService.updateOrderItemStatus(item.id, newStatus);
      setOrders((prev) =>
        prev.map((o) =>
          o.id === order.id
            ? { ...o, items: o.items.map((it) => (it.id === item.id ? { ...it, status: newStatus } : it)) }
            : o
        )
      );
    } catch (error) {
      console.error('Error updating item status:', error);
      toast({
        title: 'Error',
        description: 'Failed to update item status',
        variant: 'destructive',
      });
    }
  };

  const handleCancelOrder = async (order) => {
    if (!(await confirm({ description: `Cancel order ${order.order_number}? Its number stays reserved as cancelled.`, confirmText: 'Cancel order', tone: 'destructive' }))) return;
    try {
      await orderService.cancelOrder(order.id);
      setOrders((prev) => prev.map((o) => (o.id === order.id ? { ...o, status: 'cancelled' } : o)));
      toast({ title: 'Order cancelled', description: `${order.order_number} marked cancelled` });
      loadTables();
    } catch (e) {
      toast({ title: 'Error', description: e?.message || 'Failed to cancel order', variant: 'destructive' });
    }
  };

  // Audit trail viewer — {orderNumber, events: null (loading) | [...]}.
  const [auditView, setAuditView] = useState(null);
  const openAudit = async (order) => {
    setAuditView({ orderNumber: order.order_number, events: null });
    try {
      const events = await orderService.getOrderAudit(order.id);
      setAuditView({ orderNumber: order.order_number, events });
    } catch (e) {
      setAuditView(null);
      toast({ title: 'Error', description: e?.message || 'Failed to load order history', variant: 'destructive' });
    }
  };

  // One human-readable line per audit event.
  const auditLine = (e) => {
    const d = e.details || {};
    const items = (d.items || [])
      .map((it) => `${it.qty} × ${it.name}`)
      .join(', ');
    switch (e.event) {
      case 'CREATED':
        return `Order created${items ? ` — ${items}` : ''}${d.total != null ? ` (total ${formatCurrency(d.total)})` : ''}`;
      case 'ITEMS_ADDED':
        return `Items added${items ? ` — ${items}` : ''}${d.new_total != null ? ` (total now ${formatCurrency(d.new_total)})` : ''}${d.status_reset_to ? ' · order sent back to the kitchen' : ''}`;
      case 'STATUS_CHANGED':
        return `Status changed: ${d.from || '?'} → ${d.to || '?'}`;
      case 'ITEM_STATUS_CHANGED':
        return `${d.item || 'Item'}: ${d.from || '?'} → ${d.to || '?'}`;
      case 'CANCELLED':
        return `Order cancelled${d.reason ? ` — ${d.reason}` : ''}`;
      default:
        return e.event;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'cancelled': return 'text-red-600 bg-red-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'preparing': return 'text-blue-600 bg-blue-100';
      case 'ready': return 'text-green-600 bg-green-100';
      case 'served': return 'text-purple-600 bg-purple-100';
      case 'completed': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <AlertCircle className="w-4 h-4" />;
      case 'preparing': return <Clock className="w-4 h-4" />;
      case 'ready': return <CheckCircle className="w-4 h-4" />;
      case 'served': return <CheckCircle className="w-4 h-4" />;
      default: return <AlertCircle className="w-4 h-4" />;
    }
  };

  // The drilled-into menu's full content, loaded on select and scoped to that menu only.
  const selectedMenu = selectedMenuContent;
  const orderCategories = [];
  const seenCat = new Set();
  // Section name → its serving-time label (so the section list can show timings).
  const sectionWindows = {};
  (selectedMenu?.sections || []).forEach((s) => {
    if (!seenCat.has(s.name)) {
      seenCat.add(s.name);
      orderCategories.push(s.name);
      sectionWindows[s.name] = fmtWindow(s.available_from, s.available_until, s.follow_location_hours);
    }
  });
  const allOrderItems = (selectedMenu?.sections || []).flatMap((s) =>
    (s.items || []).map((it) => ({ ...it, _category: s.name })));
  const visibleOrderItems = allOrderItems.filter((it) => {
    const matchCat = selectedCategory === 'all' || it._category === selectedCategory;
    const matchSearch = !searchQuery || (it.name || '').toLowerCase().includes(searchQuery.toLowerCase());
    return matchCat && matchSearch;
  });

  // Which floor and section are being viewed, falling back to the first so something is always shown.
  const currentFloor = floors.find((f) => f.id === selectedFloorId) || floors[0] || null;
  // The floor-plan API exposes a floor's sections under `sections`, each with its own tables.
  const sections = currentFloor?.sections || [];
  const currentSection = sections.find((s) => s.id === selectedSectionId) || sections[0] || null;
  const sectionTables = currentSection?.tables || [];
  const tableLabel = (t) => t.table_code || `#${t.id}`;

  // Real table statuses only: occupied when it has an active order, otherwise available. No mocked states.
  const tableStatusMap = {};
  sectionTables.forEach((t) => {
    tableStatusMap[t.id] = tableBusy[t.id] ? 'occupied' : 'available';
  });

  const LegendDot = ({ color, label }) => (
    <span className="flex items-center gap-1.5 whitespace-nowrap">
      <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: color }} /> {label}
    </span>
  );

  // Free and total seat counts per section, so every floor map in the rail shows its remaining room.
  const sectionStats = (s) => {
    const list = s.tables || [];
    return { free: list.filter((t) => !tableBusy[t.id]).length, total: list.length };
  };

  // Zoom the plan down until it fits the canvas viewport (never up past 100%).
  const fitZoom = () => {
    const box = canvasWrapRef.current;
    if (!box || !currentSection) return;
    const w = (currentSection.canvas_width || 1200) + 48;
    const h = (currentSection.canvas_height || 800) + 48;
    const next = Math.min(1, box.clientWidth / w, box.clientHeight / h);
    setZoom(Math.max(ZOOM_MIN, Number(next.toFixed(2))));
  };

  const posNavItem = ({ key, label, hint, icon: Icon, to }) => {
    const active = key === 'create';
    return (
      <button
        key={key}
        type="button"
        onClick={() => !active && navigate(to)}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
          active
            ? 'bg-primary/10 text-primary font-semibold'
            : 'text-muted-foreground hover:bg-accent hover:text-foreground'}`}
      >
        <Icon size={18} className="shrink-0" />
        <span className="min-w-0">
          <span className="block text-sm leading-tight truncate">{label}</span>
          <span className="block text-[11px] opacity-70 truncate">{hint}</span>
        </span>
      </button>
    );
  };

  // Create New Order as a POS shell: a fixed rail, the floor canvas, and a selection action bar pinned at the bottom.
  const renderCreateOrderTab = () => {
    if (selectedTable) return renderOrderBuilder();
    const staged = pendingTable;
    return (
    // Exactly one viewport minus the header; -mb-28 cancels the FAB safe area so the shell doesn't scroll.
    <div className="flex h-[calc(100vh-3.5rem)] min-h-[560px] -mb-28 bg-background text-foreground">
      {/* ── Left rail: POS nav + every floor map in this location ───────── */}
      <aside className="w-60 shrink-0 bg-card border-r border-border flex flex-col">
        <nav className="p-2 space-y-1 border-b border-border">
          {POS_NAV.map(posNavItem)}
        </nav>

        <div className="px-3 pt-3 pb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          <MapPin className="w-3.5 h-3.5" />
          <span className="truncate">{location?.name || 'Floor maps'}</span>
        </div>

        <div className="flex-1 overflow-y-auto px-2 pb-3">
          {isLoading ? (
            <div className="text-sm text-muted-foreground py-8 text-center">Loading floor maps…</div>
          ) : !locationId ? (
            <div className="rounded-lg border border-dashed border-border p-4 text-xs text-muted-foreground text-center">
              No location selected.
            </div>
          ) : floors.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border p-4 text-xs text-muted-foreground text-center">
              No floor plan yet. Design one in Locations → Floor Plan.
            </div>
          ) : (
            /* Every floor with its sections listed — all maps one tap away. */
            floors.map((f) => (
              <div key={f.id} className="mb-3">
                <div className="px-2 py-1 text-xs font-semibold text-foreground/80">{f.name}</div>
                {(f.sections || []).length === 0 ? (
                  <p className="px-2 text-[11px] text-muted-foreground">No sections on this floor.</p>
                ) : (
                  <div className="space-y-0.5">
                    {(f.sections || []).map((s) => {
                      const isCurrent = currentSection?.id === s.id && currentFloor?.id === f.id;
                      const { free, total } = sectionStats(s);
                      return (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => { setSelectedFloorId(f.id); setSelectedSectionId(s.id); }}
                          className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-xs transition-colors ${
                            isCurrent
                              ? 'bg-primary/10 text-primary font-medium'
                              : 'text-muted-foreground hover:bg-accent hover:text-foreground'}`}
                        >
                          <span className="flex-1 truncate">{s.name}</span>
                          <span className={`text-[10px] tabular-nums ${free === 0 ? 'text-destructive' : 'opacity-70'}`}>
                            {free}/{total}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </aside>

      {/* ── Main: step bar → legend/zoom → floor canvas → action bar ────── */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Step bar */}
        <div className="flex items-center gap-3 border-b border-border bg-card px-5 py-2.5">
          <h2 className="text-sm font-bold shrink-0">Take an Order</h2>
          <div className="flex items-center gap-1.5">
            {POS_STEPS.map((s, i) => (
              <React.Fragment key={s.key}>
                {i > 0 && <span className="text-muted-foreground/40 text-xs">›</span>}
                <span
                  className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                    s.key === 'table'
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground'}`}
                >
                  {s.key === 'table' && staged ? <Check className="w-3 h-3 inline -mt-0.5 mr-1" /> : null}
                  {s.label}
                </span>
              </React.Fragment>
            ))}
          </div>
          <div className="ml-auto text-xs text-muted-foreground truncate">
            {currentFloor && currentSection
              ? `${currentFloor.name} · ${currentSection.name}`
              : 'No floor map'}
          </div>
        </div>

        {/* Legend + zoom */}
        <div className="flex items-center gap-4 border-b border-border bg-card/60 px-5 py-1.5 text-[11px] text-muted-foreground">
          <LegendDot color="#059669" label="Available" />
          <LegendDot color="#b91c1c" label="Occupied" />
          <LegendDot color="#2563eb" label="Selected" />
          {currentSection && (
            <span className="hidden sm:inline opacity-80">
              {sectionTables.length} tables · {sectionTables.reduce((n, t) => n + (t.capacity || 0), 0)} seats
            </span>
          )}
          <div className="ml-auto flex items-center gap-1">
            <button type="button" title="Zoom out"
              onClick={() => setZoom((z) => Math.max(ZOOM_MIN, Number((z - 0.1).toFixed(2))))}
              className="p-1.5 rounded-md hover:bg-accent hover:text-foreground transition-colors">
              <ZoomOut size={14} />
            </button>
            <span className="w-11 text-center tabular-nums">{Math.round(zoom * 100)}%</span>
            <button type="button" title="Zoom in"
              onClick={() => setZoom((z) => Math.min(ZOOM_MAX, Number((z + 0.1).toFixed(2))))}
              className="p-1.5 rounded-md hover:bg-accent hover:text-foreground transition-colors">
              <ZoomIn size={14} />
            </button>
            <button type="button" title="Fit to screen" onClick={fitZoom}
              className="p-1.5 rounded-md hover:bg-accent hover:text-foreground transition-colors">
              <Maximize2 size={14} />
            </button>
            <button type="button" title="Reset zoom to 100%" onClick={() => setZoom(1)}
              className="p-1.5 rounded-md hover:bg-accent hover:text-foreground transition-colors">
              <RotateCcw size={14} />
            </button>
          </div>
        </div>

        {/* Floor canvas */}
        <div ref={canvasWrapRef} className="flex-1 min-h-0 overflow-hidden">
          {currentSection && sectionTables.length > 0 ? (
            <SeatingFloorView
              section={currentSection}
              statusMap={tableStatusMap}
              selectedTableId={staged?.id}
              zoom={zoom}
              onSelect={(table) => setPendingTable({
                ...table,
                floor_name: currentFloor?.name,
                section_name: currentSection?.name,
              })}
            />
          ) : (
            <div className="h-full flex items-center justify-center">
              <div className="text-center text-muted-foreground">
                <ShoppingCart size={64} className="mx-auto mb-4 opacity-25" />
                <p className="text-lg">
                  {!locationId
                    ? 'Select a location to load its floor plan'
                    : floors.length === 0
                      ? 'No floor plan yet — design one in Locations → Floor Plan'
                      : 'No tables in this section'}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Action bar with the staged table and Reset / Next; pr-24 keeps the buttons clear of the floating help FAB. */}
        <div className="shrink-0 border-t border-border bg-card pl-5 pr-24 py-3 flex items-center gap-4">
          <div className="min-w-0 flex items-center gap-3">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground shrink-0">
              Selected
            </span>
            {staged ? (
              <span className="min-w-0">
                <span className="block text-sm font-semibold truncate">{tableLabel(staged)}</span>
                <span className="block text-[11px] text-muted-foreground truncate">
                  {[staged.floor_name, staged.section_name].filter(Boolean).join(' · ')}
                </span>
              </span>
            ) : (
              <span className="text-sm text-muted-foreground">
                Tap an available table on the floor map
              </span>
            )}
          </div>

          {staged && (
            <span className="flex items-center gap-1.5 text-sm text-muted-foreground shrink-0">
              <Armchair size={15} /> {staged.capacity} seats
            </span>
          )}

          <div className="ml-auto flex items-center gap-2 shrink-0">
            <Button variant="outline" size="sm" disabled={!staged}
              onClick={() => setPendingTable(null)}>
              <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset
            </Button>
            <Button size="sm" disabled={!staged} onClick={() => selectTable(staged)}>
              Next <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
  };

  // The full-page order builder once a table is picked: party size, then menu and cart, then place.
  const renderOrderBuilder = () => (
    <div className="flex flex-col h-[calc(100vh-3.5rem)] min-h-[560px] -mb-28 bg-white">
        <div className="flex items-center justify-between gap-4 border-b border-gray-200 px-5 py-3">
          <div className="flex items-center gap-3 min-w-0">
            <button type="button" onClick={resetOrderFlow}
              className="text-sm text-gray-600 hover:text-gray-900 whitespace-nowrap">← Tables</button>
            <div className="min-w-0">
              <h2 className="text-base font-bold text-gray-900 truncate">
                {selectedTable ? `${tableLabel(selectedTable)} · ${selectedTable.capacity} seats` : 'New order'}
              </h2>
              <p className="text-xs text-gray-500 truncate">
                {[selectedTable?.floor_name, selectedTable?.section_name].filter(Boolean).join(' / ')}
                {partyConfirmed && partySize ? ` · ${partySize} guest(s)` : ''}
              </p>
            </div>
          </div>
          {/* The same step chips as the floor map, so the waiter always sees how far along the order is. */}
          <div className="hidden md:flex items-center gap-1.5 shrink-0">
            {POS_STEPS.map((s, i) => {
              const current = !partyConfirmed ? 'guests' : 'menu';
              const order = POS_STEPS.findIndex((x) => x.key === current);
              const state = i < order ? 'done' : i === order ? 'active' : 'todo';
              return (
                <React.Fragment key={s.key}>
                  {i > 0 && <span className="text-gray-300 text-xs">›</span>}
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                    state === 'active' ? 'bg-orange-500 text-white'
                      : state === 'done' ? 'text-emerald-700' : 'text-gray-400'}`}>
                    {state === 'done' ? <Check className="w-3 h-3 inline -mt-0.5 mr-1" /> : null}
                    {s.label}
                  </span>
                </React.Fragment>
              );
            })}
          </div>
          {partyConfirmed && (
            <div className="text-right">
              <div className="text-[11px] text-gray-500">Total</div>
              <div className="text-base font-semibold text-gray-700">
                {formatCurrency ? formatCurrency(getTotalAmount()) : getTotalAmount()}
              </div>
            </div>
          )}
        </div>

        {!partyConfirmed ? (
          /* Step 1 — number of guests */
          <div className="flex-1 flex flex-col items-center justify-center text-center p-10">
            <Users className="h-10 w-10 text-orange-500 mb-2" />
            <h3 className="text-lg font-semibold text-gray-800">How many guests?</h3>
            <p className="text-sm text-gray-500 mb-6">Up to {selectedTable?.capacity} seats at this table</p>
            <div className="flex items-center justify-center gap-3 mb-6">
              <button type="button" onClick={() => setPartySize((p) => String(Math.max(1, (Number(p) || 1) - 1)))}
                className="h-12 w-12 rounded-full border border-gray-300 text-2xl text-gray-700 hover:bg-gray-50">−</button>
              <input type="number" min="1" value={partySize}
                onChange={(e) => setPartySize(e.target.value)}
                placeholder={String(selectedTable?.capacity || 2)}
                className="w-24 text-center text-3xl font-bold border-2 border-gray-200 rounded-lg py-1 focus:outline-none focus:border-orange-400" />
              <button type="button" onClick={() => setPartySize((p) => String((Number(p) || 0) + 1))}
                className="h-12 w-12 rounded-full border border-gray-300 text-2xl text-gray-700 hover:bg-gray-50">+</button>
            </div>
            {/* Optional guest identity — phone links this order to their CRM profile */}
            <div className="w-full max-w-xs space-y-2 mb-5 text-left">
              <label className="block text-xs font-medium text-gray-500">
                Guest details
              </label>
              <input type="text" value={guestName} onChange={(e) => setGuestName(e.target.value)}
                placeholder="Guest name"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-400" />
              <input type="tel" inputMode="numeric" maxLength={8} value={guestPhone}
                onChange={(e) => setGuestPhone(sanitizeMuLocal(e.target.value))}
                placeholder="XXXX XXXX (guest mobile)"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-400" />
            </div>
            <button type="button"
              onClick={() => { const n = Number(partySize) || selectedTable?.capacity || 2; setPartySize(String(n)); setPartyConfirmed(true); }}
              className="w-full max-w-xs py-3 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600">
              Continue to menu
            </button>
          </div>
        ) : !selectedMenuId ? (
          /* Step 2a — choose which menu to order from */
          <div className="flex-1 overflow-y-auto p-5">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <h3 className="text-base font-semibold text-gray-800 mb-1">Choose a menu</h3>
              </div>
              {/* Default to the menus orderable right now; let staff reveal the rest. */}
              <label className="shrink-0 inline-flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={showInactiveMenus}
                  onChange={(e) => setShowInactiveMenus(e.target.checked)}
                  className="h-4 w-4 accent-orange-500"
                />
                Show inactive / off-hours
              </label>
            </div>
            {orderMenuLoading ? (
              <div className="text-center text-gray-500 py-10">Loading menus…</div>
            ) : orderMenu.length === 0 ? (
              <div className="text-center text-gray-400 py-10">
                No menus for this location yet. Build one under Menu &amp; Dishes.
              </div>
            ) : (() => {
              // Time filter: by default only menus orderable right now, with a toggle for inactive or out-of-window ones.
              const visibleMenus = orderMenu.filter(
                (m) => showInactiveMenus || isMenuAvailableNow(m),
              );
              if (visibleMenus.length === 0) {
                return (
                  <div className="text-center text-gray-400 py-10">
                    No menus are active right now.{' '}
                    <button type="button" className="underline hover:text-gray-600"
                      onClick={() => setShowInactiveMenus(true)}>
                      Show all menus
                    </button>
                  </div>
                );
              }
              return (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {visibleMenus.map((m) => {
                  const availableNow = isMenuAvailableNow(m);
                  const window = fmtWindow(m.active_from, m.active_until, m.follow_location_hours);
                  return (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => { setSelectedMenuId(m.id); setSelectedCategory('all'); setSearchQuery(''); }}
                      className={`text-left bg-white border rounded-xl p-4 hover:shadow-sm transition-all ${availableNow ? 'border-orange-300 ring-1 ring-orange-200' : 'border-gray-200 hover:border-orange-300'}`}
                    >
                      <div className="flex items-center gap-2 mb-1.5">
                        <div className="h-9 w-9 rounded-lg bg-orange-50 text-orange-500 flex items-center justify-center shrink-0">
                          <UtensilsCrossed size={18} />
                        </div>
                        <h4 className="font-semibold text-gray-800 truncate flex-1">{m.name}</h4>
                        {availableNow ? (
                          <span className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">
                            <span className="h-1.5 w-1.5 rounded-full bg-green-500" /> Active now
                          </span>
                        ) : m.is_active === false ? (
                          <span className="shrink-0 text-[11px] font-medium text-gray-400 bg-gray-50 border border-gray-200 rounded-full px-2 py-0.5">Inactive</span>
                        ) : (
                          <span className="shrink-0 text-[11px] font-medium text-gray-400 bg-gray-50 border border-gray-200 rounded-full px-2 py-0.5">Off-hours</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500">
                        {(m.section_count || 0)} section{(m.section_count || 0) === 1 ? '' : 's'} · {(m.item_count || 0)} item{(m.item_count || 0) === 1 ? '' : 's'}
                      </p>
                      {window && (
                        <p className="text-xs text-gray-500 mt-1 inline-flex items-center gap-1">
                          <Clock size={12} className="text-gray-400" /> {window}
                        </p>
                      )}
                    </button>
                  );
                })}
              </div>
              );
            })()}
          </div>
        ) : (
          /* Step 2b — dishes for the chosen menu + cart */
          <div className="flex flex-col flex-1 min-h-0">
            {/* Back to the menu list + current menu name */}
            <div className="flex items-center gap-2 px-5 py-2 border-b border-gray-200">
              <button type="button" onClick={() => setSelectedMenuId(null)}
                className="inline-flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-gray-900">
                <ArrowLeft size={16} /> Menus
              </button>
              {selectedMenu && (
                <span className="text-sm text-gray-400">
                  / <span className="text-gray-700 font-medium">{selectedMenu.name}</span>
                </span>
              )}
              {selectedMenu?.available_now && (
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-green-500" /> Active now
                </span>
              )}
              {(() => {
                const win = fmtWindow(selectedMenu?.active_from, selectedMenu?.active_until, selectedMenu?.follow_location_hours);
                return win ? (
                  <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                    <Clock size={12} className="text-gray-400" /> {win}
                  </span>
                ) : null;
              })()}
            </div>
            {/* Added items pinned on top */}
            <div className="border-b border-gray-200 bg-orange-50/40 px-5 py-2.5">
              {cart.length === 0 ? (
                <p className="text-xs text-gray-400">No items yet — pick dishes below.</p>
              ) : (
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {cart.map((c) => (
                    <div key={c.key} className="flex items-center gap-2 shrink-0 bg-white border border-orange-200 rounded-lg px-2 py-1">
                      <span className="text-xs font-medium text-gray-800 whitespace-nowrap">
                        {c.dishName}<span className="text-gray-500"> · {c.variantName}</span>
                        {(c.addons || []).length > 0 && (
                          <span className="text-amber-600"> + {c.addons.map((a) => a.name).join(', ')}</span>
                        )}
                      </span>
                      <div className="flex items-center gap-1">
                        <button onClick={() => changeQty(c.key, -1)} className="h-5 w-5 rounded bg-gray-50 border text-sm leading-none">−</button>
                        <span className="text-xs font-semibold w-4 text-center">{c.qty}</span>
                        <button onClick={() => changeQty(c.key, 1)} className="h-5 w-5 rounded bg-gray-50 border text-sm leading-none">+</button>
                      </div>
                      <button onClick={() => removeFromCart(c.key)} className="text-red-400 hover:text-red-600"><X size={13} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Search */}
            <div className="px-5 pt-3">
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search dishes…"
                  className="w-full pl-10 pr-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-orange-400" />
              </div>
            </div>

            {/* Two-pane: sections on the left, dishes on the right */}
            <div className="flex flex-1 min-h-0 px-5 pb-4 gap-4">
              {/* Left — section list */}
              <aside className="w-44 shrink-0 overflow-y-auto border-r border-gray-100 pr-3">
                <button onClick={() => setSelectedCategory('all')}
                  className={`w-full text-left px-3 py-2 mb-1 rounded-lg text-sm font-medium transition-colors ${selectedCategory === 'all' ? 'bg-orange-50 text-orange-700' : 'text-gray-600 hover:bg-gray-50'}`}>
                  All dishes
                </button>
                {orderCategories.map((c) => {
                  const count = allOrderItems.filter((it) => it._category === c).length;
                  const win = sectionWindows[c];
                  return (
                    <button key={c} onClick={() => setSelectedCategory(c)}
                      className={`w-full text-left px-3 py-2 mb-1 rounded-lg text-sm font-medium transition-colors ${selectedCategory === c ? 'bg-orange-50 text-orange-700' : 'text-gray-600 hover:bg-gray-50'}`}>
                      <span className="flex items-center justify-between gap-2">
                        <span className="truncate">{c}</span>
                        <span className="text-[11px] text-gray-400">{count}</span>
                      </span>
                      {win && (
                        <span className="mt-0.5 flex items-center gap-1 text-[11px] font-normal text-gray-400">
                          <Clock size={11} /> {win}
                        </span>
                      )}
                    </button>
                  );
                })}
              </aside>

              {/* Right — dishes for the selected section */}
              <div className="flex-1 min-w-0 overflow-y-auto">
                {(orderMenuLoading || menuContentLoading) ? (
                  <div className="text-center text-gray-500 py-10">Loading menu…</div>
                ) : allOrderItems.length === 0 ? (
                  <div className="text-center text-gray-400 py-10">
                    This menu has no dishes yet. Add some under Menu &amp; Dishes.
                  </div>
                ) : visibleOrderItems.length === 0 ? (
                  <div className="text-center text-gray-400 py-10">No dishes match.</div>
                ) : (
                  <div className="space-y-3">
                    {visibleOrderItems.map((it) => {
                      // 86'd at this branch: shown greyed and not orderable, so the waiter can see it is off rather than missing.
                      const deactivated = it.available_here === false;
                      const hasPrice = (it.variants || []).length > 0;
                      const orderable = hasPrice && !deactivated;
                      const isOpen = expandedId === it.id;
                      return (
                      <div key={it.id} className={`bg-white rounded-xl border transition-all overflow-hidden ${isOpen ? 'border-orange-300 shadow-md' : 'border-gray-200'} ${(!hasPrice || deactivated) ? 'opacity-60' : ''}`}>
                      <button type="button"
                        onClick={() => orderable && toggleExpand(it)}
                        disabled={!orderable}
                        className={`w-full text-left flex items-start gap-4 p-3.5 transition-all ${orderable ? 'hover:bg-orange-50/40 cursor-pointer' : 'cursor-not-allowed'}`}>
                        {menuFields.image && (
                          it.image ? (
                            <img src={it.image} alt={it.name} className="h-16 w-16 rounded-lg object-cover shrink-0" />
                          ) : (
                            <div className="h-16 w-16 rounded-lg bg-gray-100 flex items-center justify-center text-gray-300 shrink-0">
                              <UtensilsCrossed size={24} />
                            </div>
                          )
                        )}
                        <div className="flex-1 min-w-0">
                          {/* Title row: name + category */}
                          <div className="flex items-baseline gap-2 flex-wrap">
                            <h4 className="font-semibold text-gray-900 text-sm leading-snug">{it.name}</h4>
                            {deactivated && (
                              <span className="text-[10px] uppercase tracking-wide font-semibold text-rose-700 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded">Deactivated</span>
                            )}
                            {menuFields.category && (
                              <span className="text-[10px] uppercase tracking-wide font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">{it._category}</span>
                            )}
                          </div>
                          {menuFields.description && it.description && (
                            <p className="text-xs text-gray-500 mt-1 leading-relaxed line-clamp-2">{it.description}</p>
                          )}
                          {(() => {
                            const chips = [];
                            if (menuFields.spicy_level && it.spicy_level) chips.push(`🌶 ${it.spicy_level}`);
                            if (menuFields.calories) {
                              const cals = [...new Set((it.variants || []).map((v) => v.calories).filter((c) => c > 0))].sort((a, b) => a - b);
                              if (cals.length) chips.push(`🔥 ${cals.length > 1 ? `${cals[0]}–${cals[cals.length - 1]}` : cals[0]} kcal`);
                            }
                            if (menuFields.total_time && (it.total_time ?? null) !== null) chips.push(`⏱ ${it.total_time}m`);
                            if (menuFields.prep_time && (it.prep_time ?? null) !== null) chips.push(`Prep ${it.prep_time}m`);
                            if (menuFields.cook_time && (it.cook_time ?? null) !== null) chips.push(`Cook ${it.cook_time}m`);
                            if (menuFields.serving_vessel && it.serving_vessel) chips.push(it.serving_vessel);
                            if (menuFields.cooking_station && it.cooking_station) chips.push(it.cooking_station);
                            if (menuFields.plating_note && it.plating_note) chips.push(it.plating_note);
                            if (menuFields.tags) (it.tags || []).forEach((tg) => chips.push(tg));
                            if (menuFields.custom_fields) {
                              (it.custom_fields || []).forEach((cf) => {
                                if (cf && (cf.key || cf.value)) chips.push(`${cf.key || ''}${cf.key && cf.value ? ': ' : ''}${cf.value || ''}`);
                              });
                            }
                            const showVeg = menuFields.veg && (it.is_vegan || it.is_vegetarian);
                            const kindLabel = menuFields.kind
                              ? ({ dish: 'Dish', beverage: 'Beverage', addon: 'Add-on', combo: 'Combo' }[it.kind] || null)
                              : null;
                            const allergens = menuFields.allergens ? (it.allergens || []) : [];
                            const showIng = menuFields.ingredients && (it.ingredients || []).length > 0;
                            if (!chips.length && !showVeg && !kindLabel && !allergens.length && !showIng) return null;
                            const chipBase = 'text-[11px] leading-none px-2 py-1 rounded-full border inline-flex items-center gap-1';
                            return (
                              <div className="flex flex-wrap items-center gap-1.5 mt-2">
                                {kindLabel && (
                                  <span className={`${chipBase} bg-indigo-50 text-indigo-700 border-indigo-100`}>{kindLabel}</span>
                                )}
                                {showVeg && (
                                  <span className={`${chipBase} ${it.is_vegan ? 'bg-emerald-50 text-emerald-900 border-emerald-200' : 'bg-green-50 text-green-700 border-green-200'}`}>
                                    <span className={`h-1.5 w-1.5 rounded-full ${it.is_vegan ? 'bg-emerald-800' : 'bg-green-500'}`} />
                                    {it.is_vegan ? 'Vegan' : 'Veg'}
                                  </span>
                                )}
                                {chips.map((c, i) => (
                                  <span key={i} className={`${chipBase} bg-gray-50 text-gray-600 border-gray-200`}>{c}</span>
                                ))}
                                {allergens.map((a, i) => (
                                  <span key={`al-${i}`} className={`${chipBase} bg-rose-50 text-rose-700 border-rose-200`}>
                                    <AlertTriangle size={10} /> {a}
                                  </span>
                                ))}
                                {showIng && (
                                  <span
                                    role="button"
                                    tabIndex={0}
                                    onClick={(e) => e.stopPropagation()}
                                    title={`Ingredients: ${it.ingredients.join(', ')}`}
                                    className={`${chipBase} bg-blue-50 text-blue-700 border-blue-200 cursor-help`}
                                  >
                                    <Info size={10} /> Ingredients
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                        <div className="shrink-0 text-right pl-2 self-center">
                          {!hasPrice ? (
                            <span className="text-xs text-gray-400">No price set</span>
                          ) : (
                            <div className="text-[11px] text-orange-600 font-medium whitespace-nowrap inline-flex items-center gap-0.5">
                              {it.variants.length} option{it.variants.length === 1 ? '' : 's'}
                              <ChevronDown size={12} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                            </div>
                          )}
                        </div>
                      </button>

                      {/* Inline variant chooser replacing the popup, showing each variant's quantity, serves, calories and price. */}
                      {hasPrice && isOpen && (
                        <div className="border-t border-gray-100 bg-gray-50/70 p-3.5 space-y-3">
                          <div className="rounded-lg border border-gray-200 overflow-hidden bg-white">
                            {/* Column headers */}
                            <div className="grid grid-cols-[1.6fr_1fr_0.7fr_0.9fr_1fr] gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200 text-[10px] uppercase tracking-wide font-semibold text-gray-500">
                              <span>Variant</span>
                              <span className="text-right">Qty / Pieces</span>
                              <span className="text-right">Serves</span>
                              <span className="text-right">Calories</span>
                              <span className="text-right">Price</span>
                            </div>
                            {(it.variants || []).map((v) => {
                              const on = expVariantId === v.id;
                              const qtyPieces = v.quantity != null
                                ? `${v.quantity}${v.unit ? ` ${v.unit}` : ''}`
                                : '—';
                              return (
                                <button key={v.id} type="button"
                                  onClick={() => { setExpVariantId(v.id); setExpAddons({}); }}
                                  className={`w-full grid grid-cols-[1.6fr_1fr_0.7fr_0.9fr_1fr] gap-2 px-3 py-2.5 text-sm items-center border-b border-gray-100 last:border-0 transition-colors ${on ? 'bg-orange-50' : 'hover:bg-gray-50'}`}>
                                  <span className="flex items-center gap-2 min-w-0">
                                    <span className={`h-3.5 w-3.5 rounded-full border-2 shrink-0 ${on ? 'border-orange-500 bg-orange-500' : 'border-gray-300'}`} />
                                    <span className={`truncate ${on ? 'font-semibold text-orange-700' : 'text-gray-800'}`}>{v.name}</span>
                                    {v.diet && (
                                      <span
                                        className={`shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[10px] font-medium ${dietMeta(v.diet).badge}`}
                                        title={`${dietMeta(v.diet).label} variant`}
                                      >
                                        <span className={`h-1.5 w-1.5 rounded-full ${dietMeta(v.diet).dot}`} />
                                        {dietMeta(v.diet).label}
                                      </span>
                                    )}
                                  </span>
                                  <span className="text-right text-gray-600 tabular-nums">{qtyPieces}</span>
                                  <span className="text-right text-gray-600 tabular-nums">{v.serves ?? '—'}</span>
                                  <span className="text-right text-gray-600 tabular-nums">{v.calories ? `${v.calories}` : '—'}</span>
                                  <span className="text-right font-medium text-gray-600 whitespace-nowrap">
                                    {v.is_free ? 'Free' : (formatCurrency ? formatCurrency(v.price) : v.price)}
                                  </span>
                                </button>
                              );
                            })}
                          </div>

                          {/* Paid add-ons for the chosen variant: optional and multi-select, each ticked one bumping the line price. */}
                          {(() => {
                            const selV = (it.variants || []).find((v) => v.id === expVariantId);
                            const addons = selV?.addons || [];
                            if (!selV || addons.length === 0) return null;
                            return (
                              <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3 space-y-2">
                                <div className="text-[10px] uppercase tracking-wide font-semibold text-gray-600">
                                  Add-ons <span className="font-normal normal-case text-gray-400">· optional · priced for {selV.name}</span>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                                  {addons.map((a) => {
                                    const on = !!expAddons[a.addon_id];
                                    return (
                                      <label key={a.addon_id}
                                        className={`flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md border cursor-pointer text-sm transition-colors ${on ? 'border-amber-400 bg-amber-100/60' : 'border-gray-200 bg-white hover:border-amber-300'}`}>
                                        <span className="flex items-center gap-2 min-w-0">
                                          <input type="checkbox" checked={on}
                                            onChange={() => setExpAddons((m) => {
                                              const next = { ...m };
                                              if (next[a.addon_id]) delete next[a.addon_id];
                                              else next[a.addon_id] = { addon_id: a.addon_id, name: a.name, price: Number(a.price) || 0, price_net: Number(a.price_net ?? a.price) || 0 };
                                              return next;
                                            })}
                                            className="h-3.5 w-3.5 shrink-0" />
                                          <span className="truncate font-medium text-gray-800" title={a.name}>{a.name}</span>
                                        </span>
                                        <span className="text-gray-600 whitespace-nowrap tabular-nums">
                                          + {formatCurrency ? formatCurrency(a.price) : a.price}
                                        </span>
                                      </label>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })()}

                          {/* Complimentary items fetched from the dish controller; one pick per kind is MANDATORY. */}
                          {expCompLoading ? (
                            <div className="rounded-lg border border-gray-200 bg-white p-3 text-xs text-gray-500">
                              Loading complimentary items…
                            </div>
                          ) : expCompOptions.length > 0 && (() => {
                            const groups = {};
                            expCompOptions.forEach((c) => {
                              const k = c.kind || 'other';
                              if (!groups[k]) groups[k] = [];
                              groups[k].push(c);
                            });
                            const kindLabel = { dish: 'Dish', beverage: 'Beverage', addon: 'Add-on', combo: 'Combo', other: 'Complimentary' };
                            return (
                              <div className="rounded-lg border border-orange-200 bg-orange-50/40 p-3 space-y-2">
                                <div className="text-[10px] uppercase tracking-wide font-semibold text-gray-600">
                                  Complimentary <span className="font-normal normal-case text-rose-500">· required · one per type</span>
                                </div>
                                {Object.entries(groups).map(([kind, opts]) => (
                                  <div key={kind} className="flex items-center gap-2">
                                    <span className="text-[11px] text-gray-600 w-20 shrink-0">{kindLabel[kind] || kind}</span>
                                    <div className="relative flex-1">
                                      <select
                                        value={expComp[kind]?.id ?? ''}
                                        onChange={(e) => {
                                          const id = Number(e.target.value);
                                          const picked = opts.find((o) => o.id === id) || null;
                                          setExpComp((m) => ({ ...m, [kind]: picked }));
                                        }}
                                        className={`w-full appearance-none rounded-md border bg-white px-3 py-1.5 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 ${expComp[kind] ? 'border-gray-300 text-gray-800' : 'border-rose-300 text-gray-500'}`}
                                      >
                                        <option value="">Select {(kindLabel[kind] || kind).toLowerCase()}… (free)</option>
                                        {opts.map((c) => (
                                          <option key={c.id} value={c.id}>{c.name}</option>
                                        ))}
                                      </select>
                                      <ChevronDown size={14} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            );
                          })()}

                          {/* Quantity + add */}
                          <div className="flex items-center gap-3">
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Qty</span>
                            <div className="flex items-center gap-2">
                              <button type="button" onClick={() => setExpQty((q) => Math.max(1, q - 1))}
                                className="h-8 w-8 rounded-full border border-gray-300 text-gray-700 hover:bg-gray-100 inline-flex items-center justify-center">−</button>
                              <span className="w-8 text-center text-base font-bold">{expQty}</span>
                              <button type="button" onClick={() => setExpQty((q) => q + 1)}
                                className="h-8 w-8 rounded-full border border-gray-300 text-gray-700 hover:bg-gray-100 inline-flex items-center justify-center">+</button>
                            </div>
                            <button type="button" onClick={() => confirmExpanded(it)}
                              disabled={!expVariantId || expCompLoading || !allComplimentaryChosen}
                              title={!allComplimentaryChosen ? 'Choose a complimentary item for each type' : undefined}
                              className="flex-1 py-2.5 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed">
                              {allComplimentaryChosen ? `Add ${expQty} to order` : 'Choose complimentary first'}
                            </button>
                          </div>
                        </div>
                      )}
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Footer — place order. pr-24 clears the floating Dodo help FAB. */}
            <div className="border-t border-gray-200 pl-5 pr-24 py-3 flex items-center justify-between">
              <button type="button" onClick={() => { setPartyConfirmed(false); }}
                className="text-sm text-gray-600 hover:text-gray-900">← Guests</button>
              <button type="button" onClick={handleSaveOrder} disabled={cart.length === 0}
                className="px-5 py-2.5 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 disabled:opacity-50 flex items-center gap-2">
                <Check size={18} /> Place Order · {formatCurrency ? formatCurrency(getTotalAmount()) : getTotalAmount()}
              </button>
            </div>
          </div>
        )}
    </div>
  );

  return (
    <div className="w-full">
      <div className="bg-white">
        {/* No page header: the create tab is a full-height POS shell with its own step bar. */}
        {activeTab !== 'create' && (
        <div className="border-b border-gray-200 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
              <ShoppingCart className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-display text-3xl font-semibold text-foreground tracking-tight leading-none">
                {activeTab === 'active' ? 'Active Orders' : 'All Orders'}
              </h1>
              <p className="text-gray-500">
                {activeTab === 'active'
                  ? 'Orders whose payment is not settled yet'
                  : "Every order of the day — active and completed"}
              </p>
            </div>
          </div>
        </div>
        )}

        {/* The view is selected from the sidebar, so there is no in-page tab bar. */}

        {/* Tab Content */}
        <div className="">
          {activeTab === 'create' && renderCreateOrderTab()}

          {(activeTab === 'orders' || activeTab === 'active') && (
            <div className="p-6">
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-2">
                  {activeTab === 'active' ? 'Active Orders' : "Today's Orders"}
                </h2>
                <p className="text-gray-600">
                  {activeTab === 'active'
                    ? 'Unpaid orders — everything still open before the bill is settled'
                    : 'All orders placed today, including completed and cancelled ones'}
                </p>
              </div>

              {isLoading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading orders...</p>
                </div>
              ) : orders.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <ShoppingCart className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-xl font-semibold mb-2">
                    {activeTab === 'active' ? 'No Active Orders' : 'No Orders Today'}
                  </h3>
                  <p>
                    {activeTab === 'active'
                      ? 'No unpaid orders right now'
                      : 'No orders have been placed today yet'}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 items-start">
                  {/* One card per order in a fixed-height grid, with unstarted orders sorting first, then by how long they've waited. */}
                  {[...orders]
                    .sort((a, b) => {
                      const rank = { pending: 0, preparing: 1, ready: 2, served: 3, delivered: 3, completed: 4, cancelled: 5 };
                      const ra = rank[a.status] ?? 9;
                      const rb = rank[b.status] ?? 9;
                      if (ra !== rb) return ra - rb;
                      return new Date(a.created_at) - new Date(b.created_at);
                    })
                    .map((order) => {
                      const info = tableInfo[order.table_id] || {};
                      const orderIsLive = !['cancelled', 'delivered', 'completed'].includes(order.status);
                      return (
                    <div key={order.id} className="border rounded-lg bg-white shadow-sm flex flex-col h-[320px]">
                      {/* Header: number + status, then table / guest / time */}
                      <div className="px-4 pt-3 pb-2 border-b border-gray-100">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-semibold font-mono text-sm">{order.order_number}</span>
                          <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold flex items-center gap-1 shrink-0 ${getStatusColor(order.status)}`}>
                            {getStatusIcon(order.status)}
                            {order.status.toUpperCase()}
                          </span>
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500">
                          <span className="inline-flex items-center gap-1 font-semibold text-gray-700">
                            <MapPin size={12} className="text-orange-500" />
                            {orderOrigin(t, order)}
                          </span>
                          {info.isShared && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-yellow-100 text-yellow-800 border border-yellow-300">
                              <Users size={10} />
                              SHARED · {(info.sessions || []).length}
                            </span>
                          )}
                          <span className="min-w-0 truncate">
                            {order.customer_name ? `${order.customer_name} · ` : ''}
                            {formatDateTime(order.created_at)}
                          </span>
                        </div>
                      </div>

                      {/* Items scroll inside the fixed-height card, each live item carrying its own next-step button. */}
                      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-2 space-y-1.5">
                        {order.items.map((item, index) => {
                          // A terminal order overrides stale per-item statuses, so nothing lingers as PENDING after it is served or cancelled.
                          const itemStatus =
                            order.status === 'cancelled' ? 'CANCELLED'
                            : ['delivered', 'completed'].includes(order.status) ? 'SERVED'
                            : item.status;
                          const nextStatus = orderIsLive && item.id ? ITEM_NEXT_STATUS[itemStatus] : null;
                          const nextLabel = { PENDING: 'Prepare', PREPARING: 'Ready', READY: 'Serve' }[itemStatus];
                          /* Who called this dish off. The kitchen refusing a
                             dish and the floor pulling it are different facts
                             to a guest asking why, so the line says which. */
                          const cancelledBy = itemStatus === 'CANCELLED' ? item.cancelled_by : '';
                          const cancelledLabel = { KITCHEN: 'Cancelled by kitchen', FLOOR: 'Cancelled by floor', COUNTER: 'Cancelled at counter' }[cancelledBy];
                          return (
                          <div key={item.id ?? index} className="flex flex-col gap-0.5 text-xs text-gray-700">
                            <div className="flex items-center justify-between gap-2">
                            <span className={`min-w-0 truncate ${itemStatus === 'CANCELLED' ? 'line-through text-gray-400' : ''}`} title={item.name}>
                              {item.quantity} × {item.name}
                            </span>
                            <span className="flex items-center gap-2 shrink-0">
                              <span className={`text-[10px] font-semibold ${
                                { PENDING: 'text-amber-600', PREPARING: 'text-orange-600', READY: 'text-green-600', SERVED: 'text-gray-400', CANCELLED: 'text-red-500' }[itemStatus] || 'text-gray-500'
                              }`}>
                                {itemStatus}
                              </span>
                              {/* Struck through, because a cancelled dish has
                                  already come OFF the total below it — the
                                  figure is what it would have cost, not owed. */}
                              <span className={itemStatus === 'CANCELLED' ? 'text-gray-400 line-through' : 'text-gray-500'}>
                                {formatCurrency(item.price * item.quantity)}
                              </span>
                              {nextStatus && (
                                <button
                                  type="button"
                                  onClick={() => updateItemStatus(order, item, nextStatus)}
                                  title={`Mark this item ${nextStatus.toLowerCase()}`}
                                  className={`h-6 px-2 rounded-md text-[11px] font-semibold text-white ${
                                    { PENDING: 'bg-blue-600 hover:bg-blue-700', PREPARING: 'bg-green-600 hover:bg-green-700', READY: 'bg-purple-600 hover:bg-purple-700' }[itemStatus]
                                  }`}
                                >
                                  {nextLabel}
                                </button>
                              )}
                            </span>
                            </div>
                            {cancelledLabel && (
                              <span className="text-[10px] text-red-600">
                                {cancelledLabel}
                                {item.cancel_reason ? ` — ${item.cancel_reason}` : ''}
                              </span>
                            )}
                          </div>
                          );
                        })}
                        {order.notes && (
                          <div className="rounded-md bg-amber-50 border border-amber-200 px-2.5 py-1 text-xs text-amber-800">
                            <span className="font-semibold">Guest note:</span> {order.notes}
                          </div>
                        )}
                      </div>

                      {/* Footer: total and whole-order actions, showing the net owed when a paid booking advance comes off the bill. */}
                      <div className="border-t border-gray-100 px-4 py-2.5 flex items-center gap-2">
                        {order.advance_amount > 0 ? (
                          <span className="min-w-0">
                            <span className="font-bold text-sm">
                              {formatCurrency(Math.max(0, order.total_amount - order.advance_amount))}
                            </span>
                            <span
                              className="block text-[10px] leading-tight text-emerald-700"
                              title={order.advance_status === 'APPLIED'
                                ? 'Booking advance already adjusted in this bill'
                                : 'Paid with the reservation — deducted when the bill is settled'}
                            >
                              {formatCurrency(order.total_amount)} − {formatCurrency(order.advance_amount)} advance
                              {order.advance_status === 'APPLIED' ? ' (adjusted)' : ''}
                            </span>
                          </span>
                        ) : (
                          <span className="font-bold text-sm">{formatCurrency(order.total_amount)}</span>
                        )}
                        <span className="ml-auto flex items-center gap-1.5">
                          {order.status === 'pending' && (
                            <Button
                              size="sm"
                              onClick={() => updateOrderStatus(order.id, 'preparing')}
                              className="h-7 px-2.5 text-xs bg-blue-600 hover:bg-blue-700"
                            >
                              Start Preparing
                            </Button>
                          )}
                          {order.status === 'preparing' && (
                            <Button
                              size="sm"
                              onClick={() => updateOrderStatus(order.id, 'ready')}
                              className="h-7 px-2.5 text-xs bg-green-600 hover:bg-green-700"
                            >
                              Mark Ready
                            </Button>
                          )}
                          {order.status === 'ready' && (
                            <Button
                              size="sm"
                              onClick={() => updateOrderStatus(order.id, 'served')}
                              className="h-7 px-2.5 text-xs bg-purple-600 hover:bg-purple-700"
                            >
                              Mark Served
                            </Button>
                          )}
                          {order.status === 'delivered' && (
                            <Button
                              size="sm"
                              onClick={() => updateOrderStatus(order.id, 'completed')}
                              className="h-7 px-2.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                            >
                              Mark Paid
                            </Button>
                          )}
                          {orderIsLive && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleCancelOrder(order)}
                              className="h-7 px-2 text-xs text-red-600 border-red-200 hover:bg-red-50"
                            >
                              <Ban className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openAudit(order)}
                            title="Order history — who did what, when"
                            className="h-7 px-2 text-xs text-gray-600"
                          >
                            <History className="w-3.5 h-3.5" />
                          </Button>
                        </span>
                      </div>
                    </div>
                      );
                    })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Order audit trail */}
        <Dialog open={!!auditView} onOpenChange={(open) => !open && setAuditView(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <History className="w-4 h-4" />
                History — {auditView?.orderNumber}
              </DialogTitle>
            </DialogHeader>
            {!auditView?.events ? (
              <div className="py-8 text-center text-sm text-gray-500">Loading history…</div>
            ) : auditView.events.length === 0 ? (
              <div className="py-8 text-center text-sm text-gray-500">
                No history recorded for this order (it predates audit logging).
              </div>
            ) : (
              <div className="max-h-[60vh] overflow-y-auto space-y-3 pr-1">
                {auditView.events.map((e, i) => (
                  <div key={i} className="flex gap-3 text-sm">
                    <div className="w-1.5 shrink-0 flex flex-col items-center pt-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                      {i < auditView.events.length - 1 && (
                        <span className="flex-1 w-px bg-gray-200 mt-1" />
                      )}
                    </div>
                    <div className="pb-1 min-w-0">
                      <div className="text-gray-800">{auditLine(e)}</div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {e.at ? formatDateTime(e.at) : ''}
                        {' · '}
                        {e.actor}
                        {e.actor_type === 'guest' ? ' (guest)' : e.actor_type === 'staff' ? ' (staff)' : ''}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Success Dialog */}
        <Dialog open={showSuccess} onOpenChange={setShowSuccess}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-center">Order Saved!</DialogTitle>
            </DialogHeader>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check size={32} className="text-green-600" />
              </div>
              {placedOrderNumber && (
                <p className="text-2xl font-bold tracking-wide text-gray-800 mb-1">{placedOrderNumber}</p>
              )}
              <p className="text-muted-foreground">Your order has been placed successfully</p>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

export default OrderManagement;